import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { authFetch } from '@verantos-dev/core-utils';
import type { ObjectResponse } from '@verantos-dev/types-common';

import { analysisKeys } from './queryKeys';
import type { AnalysisProfile } from './types';
import {
  clearProcessingStart,
  hasProcessingExceededLimit,
  recordProcessingStart,
} from '../utils/processingLimit';

interface UseAnalysisProfileQueryArgs {
  projectId: string;
  analysisProfileId: string;
}

// Poll cadence while a run is in flight. Mirrors the analytical-dataset
// profile poll (useAnalyticalDatasetProfileQuery) — a worker flips
// latestStatus PROCESSING -> PROCESSED/ERROR out of band, and the profile GET
// is the signal the UI watches.
const PROCESSING_POLL_INTERVAL_MS = 5000;

// Fallback cadence when the analysis's results are being read by something on the
// page too: they reflect the run's end and refresh this profile themselves.
const PROCESSING_POLL_INTERVAL_WITH_RESULTS_MS = 15000;

// Whether anything is currently subscribed to the analysis's results.
const isResultsObserved = (queryClient: QueryClient, analysisProfileId: string): boolean =>
  (queryClient
    .getQueryCache()
    .find({ queryKey: analysisKeys.results(analysisProfileId), exact: true })
    ?.getObserversCount() ?? 0) > 0;

// Keyed on `profileId` only — same cache entry across versions.
export const useAnalysisProfileQuery = ({
  projectId,
  analysisProfileId,
}: UseAnalysisProfileQueryArgs) => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: analysisKeys.profile(analysisProfileId),
    queryFn: () =>
      authFetch<ObjectResponse<AnalysisProfile>>(
        `/analyses/${encodeURIComponent(projectId)}` +
          `/analysis-profile/${encodeURIComponent(analysisProfileId)}`
      ),
    enabled: projectId.length > 0 && analysisProfileId.length > 0,
    retry: false,
    // Auto-poll only while the latest run is PROCESSING; stop on any terminal
    // status. No external `shouldPoll` flag needed — the cached status drives it.
    // A run past PROCESSING_POLL_LIMIT_MS is overdue: stop polling it (the page
    // offers a manual re-check) instead of polling a run that may never finish.
    // While the results are being polled alongside (the analysis page), they already
    // report the run ending and trigger a re-read of this profile, so it only polls
    // slowly as a fallback.
    refetchInterval: (query) => {
      if (
        query.state.data?.data.latestStatus !== 'PROCESSING' ||
        hasProcessingExceededLimit(queryClient, analysisProfileId)
      ) {
        return false;
      }
      return isResultsObserved(queryClient, analysisProfileId)
        ? PROCESSING_POLL_INTERVAL_WITH_RESULTS_MS
        : PROCESSING_POLL_INTERVAL_MS;
    },
    // Recover a stranded PROCESSING status if the tab slept through the worker
    // finishing (matches the analytical-dataset profile query's behavior).
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  // The results table and the per-patient dataset grid are separate queries; a
  // finished run changes their cells/rows without touching their caches, so
  // refetch them when the status leaves PROCESSING. The dataset grid's keys carry
  // the dataset profile id and grid params, so it is invalidated by prefix.
  // Every component mounting this hook runs this effect on the same transition;
  // `cancelRefetch: false` joins the in-flight request instead of cancelling and
  // restarting it once per mounted hook.
  const latestStatus = query.data?.data.latestStatus;
  const previousStatus = useRef(latestStatus);
  useEffect(() => {
    if (previousStatus.current === 'PROCESSING' && latestStatus !== 'PROCESSING') {
      queryClient.invalidateQueries(
        { queryKey: analysisKeys.results(analysisProfileId) },
        { cancelRefetch: false }
      );
      queryClient.invalidateQueries(
        { queryKey: analysisKeys.datasetResultsAll() },
        { cancelRefetch: false }
      );
    }
    previousStatus.current = latestStatus;
  }, [latestStatus, analysisProfileId, queryClient]);

  // The poll limit counts from when this client began watching the run (see
  // `processingLimit`): note it when the profile is first seen PROCESSING, forget it
  // once the run is over.
  useEffect(() => {
    if (!latestStatus) return;
    if (latestStatus === 'PROCESSING') recordProcessingStart(queryClient, analysisProfileId);
    else clearProcessingStart(queryClient, analysisProfileId);
  }, [latestStatus, analysisProfileId, queryClient]);

  return query;
};
